import { useEffect, useState } from "react";
import {
  ApiClientError,
  fetchReviewItem,
  postReviewDraft,
  postReviewFlow,
  postReviewReply,
  postReviewUnqueue,
} from "../../../api/client";
import type { ReviewFlowInput, ReviewItem, ReviewNgWordsDetails } from "../../../types/reviews";
import { FlowTimeline } from "./FlowTimeline";

const RMS_URL = "https://review.rms.rakuten.co.jp/search/index/";
const MALL_LABEL: Record<string, string> = { shopify: "Shopify", rakuten: "楽天市場" };

function stars(rating: number | null): string {
  if (rating === null) return "―";
  return "★".repeat(rating) + "☆".repeat(5 - rating);
}

interface ReviewDetailProps {
  item: ReviewItem;
  owners: string[];
  onChanged: (item: ReviewItem) => void;
}

export function ReviewDetail({ item, owners, onChanged }: ReviewDetailProps) {
  const [body, setBody] = useState(item.reply.body ?? item.reply.draft ?? "");
  const [instruction, setInstruction] = useState("");
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ngHits, setNgHits] = useState<string[] | null>(null);

  useEffect(() => {
    setBody(item.reply.body ?? item.reply.draft ?? "");
    setError(null);
    setNgHits(null);
    setInstruction("");
  }, [item.key]);

  const isPosted = item.reply.status === "posted";
  const isQueued = item.reply.status === "queued";
  const over1000 = body.length > 1000;

  const generateDraft = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await postReviewDraft(item.key, instruction.trim() || undefined);
      setBody(res.draft);
      setNgHits(res.ngHits.length > 0 ? res.ngHits : null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : "AI返信の生成に失敗しました。");
    } finally {
      setGenerating(false);
    }
  };

  const submitReply = async (force = false) => {
    setPosting(true);
    setError(null);
    try {
      await postReviewReply(item.key, body, ngHits && ngHits.length > 0 ? "ai" : "manual", force);
      setNgHits(null);
      // サーバー（mockはfixture時計）側の最新状態を取り直す（楽天のqueuedAt等はクライアント時計と
      // 一致しないため、楽観更新の自前組み立てはしない）。
      const res = await fetchReviewItem(item.key);
      onChanged(res.item);
    } catch (e) {
      if (e instanceof ApiClientError && e.code === "ng_words") {
        const hits = (e.details as ReviewNgWordsDetails | undefined)?.ngHits ?? [];
        setNgHits(hits);
      } else {
        setError(e instanceof ApiClientError ? e.message : "返信の投稿に失敗しました。");
      }
    } finally {
      setPosting(false);
    }
  };

  const unqueue = async () => {
    setPosting(true);
    setError(null);
    try {
      const res = await postReviewUnqueue(item.key);
      setBody(res.item.reply.draft ?? "");
      onChanged(res.item);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : "取り消しに失敗しました。");
    } finally {
      setPosting(false);
    }
  };

  const saveFlow = async (input: ReviewFlowInput) => {
    setFlowBusy(true);
    setError(null);
    try {
      const res = await postReviewFlow(item.key, input);
      onChanged(res.item);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : "更新に失敗しました。");
    } finally {
      setFlowBusy(false);
    }
  };

  return (
    <div className="rv-detail">
      <div className="rv-detail-head">
        <div>
          <div className="rv-detail-meta">
            <span className="rv-stars">{stars(item.rating)}</span>
            <span>{MALL_LABEL[item.mall]}</span>
            <span>{item.product ?? "ショップレビュー"}</span>
            <span>{item.postedAt.slice(0, 16).replace("T", " ")}</span>
            {item.orderNumber && <span>注文番号 {item.orderNumber}</span>}
          </div>
        </div>
      </div>
      <div className="rv-detail-body">{item.body}</div>

      <div className="rv-card" style={{ padding: 0, border: "none", gap: 10 }}>
        <span className="rv-card-title" style={{ fontSize: 14 }}>
          返信
        </span>

        {isPosted ? (
          <>
            <div className="rv-detail-body">{item.reply.body}</div>
            <span className="rv-note">
              投稿済み（編集不可）
              {item.mall === "rakuten" && (
                <>
                  {" ・ "}
                  <a href={RMS_URL} target="_blank" rel="noreferrer">
                    楽天RMSで編集
                  </a>
                </>
              )}
            </span>
          </>
        ) : (
          <>
            <textarea
              className="rv-textarea"
              value={body}
              maxLength={1000}
              placeholder="返信本文（1000文字まで）"
              disabled={isQueued || posting}
              onChange={(e) => {
                setBody(e.target.value);
                setNgHits(null);
              }}
            />
            <div className={`rv-char-count ${over1000 ? "over" : ""}`}>{body.length} / 1000文字</div>

            {ngHits && ngHits.length > 0 && (
              <div className="rv-ng-banner">
                <span>NGワードの可能性があります： {ngHits.join("、")}</span>
                <div className="rv-btn-row">
                  <button className="rv-btn rv-btn-danger" disabled={posting} onClick={() => submitReply(true)}>
                    このまま投稿
                  </button>
                </div>
              </div>
            )}

            {isQueued ? (
              <>
                <span className="rv-note warn">
                  投稿待ち{item.reply.queuedAt ? `（${item.reply.queuedAt.slice(0, 16).replace("T", " ")}登録）` : ""} ・ 翌朝7:40に楽天へ投稿されます
                </span>
                <div className="rv-btn-row">
                  <button className="rv-btn" disabled={posting} onClick={unqueue}>
                    取り消す
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  className="rv-input"
                  style={{ width: "100%", boxSizing: "border-box" }}
                  placeholder="AI生成への追加指示（任意）"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  disabled={generating}
                />
                <div className="rv-btn-row">
                  <button className="rv-btn" disabled={generating} onClick={generateDraft}>
                    {generating ? "生成中…" : "AIで返信を生成"}
                  </button>
                  <button
                    className="rv-btn rv-btn-primary"
                    disabled={posting || !body.trim() || over1000}
                    onClick={() => submitReply(false)}
                  >
                    {posting ? "送信中…" : "コメントする"}
                  </button>
                  {item.reply.status === "failed" && item.reply.error && (
                    <span className="rv-note" style={{ color: "var(--rv-bad)" }}>
                      前回エラー: {item.reply.error}
                    </span>
                  )}
                </div>
              </>
            )}
          </>
        )}
        {error && <span className="rv-note" style={{ color: "var(--rv-bad)" }}>{error}</span>}
      </div>

      {item.flow && <FlowTimeline key={item.key} item={item} owners={owners} onSave={saveFlow} busy={flowBusy} />}
    </div>
  );
}
